import { useEffect } from 'react';
import { ensureFreshSession, isSessionDefinitivelyInvalid } from '@/services/auth/sessionManager';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { supabase } from '@/integrations/supabase/client';
import type { Session } from '@supabase/supabase-js';
import { clearAppCaches } from '@/utils/clearAppCaches';
import { toast } from 'sonner';
import i18n from '@/i18n';

const VERSION_KEY = 'app_last_known_version';
const PERIODIC_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min


export function useCapacitorLifecycle() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return; // Ne rien faire sur web
    }

    let appStateListener: any;
    let backButtonListener: any;
    let periodicInterval: ReturnType<typeof setInterval> | null = null;
    // Dernière version déjà proposée via toast — pour ne pas re-proposer la
    // même à chaque resume tant que l'utilisateur n'a pas redémarré.
    let promptedBuildTime: number | null = null;

    /**
     * Applique réellement la mise à jour. INVARIANT : VERSION_KEY n'avance
     * QUE ici — une mise à jour détectée mais non appliquée reste donc
     * « inconnue » et sera auto-appliquée au prochain démarrage à froid.
     */
    const applyUpdate = async (serverBuildTime: number) => {
      await Preferences.set({ key: VERSION_KEY, value: serverBuildTime.toString() });
      // ✅ Nettoyer tous les caches JS avant le reload
      await clearAppCaches();
      // Hard reload avec cache-busting URL pour forcer le rechargement complet
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.replace(`${baseUrl}?_forceUpdate=${serverBuildTime}`);
    };

    /**
     * `allowImmediateReload` : true uniquement au démarrage à froid (rien
     * n'est saisi). Au resume et sur l'intervalle périodique, on ne recharge
     * JAMAIS d'autorité — l'utilisateur peut être en pleine saisie (le bug
     * historique : basculer sur WhatsApp puis revenir effaçait le
     * formulaire). On affiche un toast persistant avec action « Redémarrer ».
     */
    const checkForUpdates = async ({ allowImmediateReload }: { allowImmediateReload: boolean }) => {
      try {
        console.log('[Lifecycle] Checking for app updates...');

        // 1. Récupérer la version depuis le serveur avec cache-busting agressif
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
        });

        if (!response.ok) return;

        const serverVersion = await response.json();
        const serverBuildTime = serverVersion.buildTime;

        // 2. Récupérer la version connue depuis le stockage persistant
        const { value: storedVersion } = await Preferences.get({ key: VERSION_KEY });
        const lastKnownVersion = storedVersion ? parseInt(storedVersion, 10) : null;

        console.log('[Lifecycle] Server version:', serverBuildTime, 'Local version:', lastKnownVersion);

        // 3. Si première fois, stocker et sortir
        if (lastKnownVersion === null) {
          await Preferences.set({ key: VERSION_KEY, value: serverBuildTime.toString() });
          console.log('[Lifecycle] Initial version stored:', serverBuildTime);
          return;
        }

        if (serverBuildTime <= lastKnownVersion) return;

        if (allowImmediateReload) {
          // Démarrage à froid : appliquer tout de suite, comme avant.
          console.log('[Lifecycle] 🎉 New version detected at startup! Applying...');
          toast.info(i18n.t('capacitorLifecycle.newVersionAvailable', { ns: 'appui' }), {
            description: i18n.t('capacitorLifecycle.updatingInProgress', { ns: 'appui' }),
            duration: 1500,
          });
          setTimeout(() => { applyUpdate(serverBuildTime); }, 1500);
          return;
        }

        // Mi-session : proposer sans imposer (une seule fois par version).
        if (promptedBuildTime === serverBuildTime) return;
        promptedBuildTime = serverBuildTime;
        console.log('[Lifecycle] 🎉 New version detected mid-session — deferring to user');
        toast.info(i18n.t('capacitorLifecycle.newVersionAvailable', { ns: 'appui' }), {
          description: i18n.t('capacitorLifecycle.restartWhenReady', { ns: 'appui' }),
          duration: Infinity,
          action: {
            label: i18n.t('capacitorLifecycle.restart', { ns: 'appui' }),
            onClick: () => { applyUpdate(serverBuildTime); },
          },
        });
      } catch (error) {
        console.error('[Lifecycle] Error checking updates:', error);
      }
    };

    const setupListeners = async () => {
      // ✅ Vérifier la version au démarrage (non-bloquant) — seul moment où
      // un reload immédiat est acceptable : rien n'est encore saisi.
      checkForUpdates({ allowImmediateReload: true });

      // ✅ Listener pour détecter quand l'app revient au foreground
      appStateListener = await CapacitorApp.addListener('appStateChange', async (state) => {
        console.log('[Capacitor] App state changed:', state.isActive ? 'ACTIVE' : 'BACKGROUND');

        if (state.isActive) {
          // ✅ Vérifier la version au retour — SANS reload d'autorité
          checkForUpdates({ allowImmediateReload: false });

          // ✅ Déclencher l'événement immédiatement pour que l'UI commence à se mettre à jour
          window.dispatchEvent(new CustomEvent('capacitor-app-resumed'));

          console.log('[Capacitor] App resumed - checking session...');

          // Vérifier et rafraîchir la session. Le storage Preferences est
          // ASYNCHRONE : juste après un resume, getSession() peut renvoyer
          // null de façon transitoire alors que la session existe — on
          // réessaie donc avant de conclure à une vraie expiration (sinon :
          // déconnexion à tort et perte du formulaire en cours).
          // Point unique : relit la session et ne la rafraîchit que si elle
          // expire bientôt. Aucune redirection ici : une session vide au
          // réveil n'est déconnectée que si le serveur confirme la révocation.
          const session = await ensureFreshSession();
          if (!session) {
            const invalid = await isSessionDefinitivelyInvalid();
            const currentPath = window.location.pathname;
            const isOnAuthPage = ['/login', '/set-password', '/forgot-password'].includes(currentPath);
            if (invalid && !isOnAuthPage) {
              toast.info(i18n.t('capacitorLifecycle.pleaseReconnect', { ns: 'appui' }), {
                description: i18n.t('capacitorLifecycle.sessionExpiredDescription', { ns: 'appui' })
              });
              window.location.href = '/login';
            }
          }
        }
      });

      // ✅ Listener pour détecter les back button Android
      backButtonListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          CapacitorApp.exitApp();
        } else {
          window.history.back();
        }
      });
    };

    setupListeners();

    // ✅ Check périodique toutes les 5 min tant que l'app est ouverte
    periodicInterval = setInterval(() => {
      checkForUpdates({ allowImmediateReload: false });
    }, PERIODIC_CHECK_INTERVAL_MS);

    // Cleanup
    return () => {
      if (appStateListener) {
        appStateListener.remove();
      }
      if (backButtonListener) {
        backButtonListener.remove();
      }
      if (periodicInterval) {
        clearInterval(periodicInterval);
      }
    };
  }, []);
}
